import React, {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithChildren,
  useContext
} from "react";

import {
  Button as MagneticButton,
  ButtonProps as MagneticButtonProps
} from "@magnetic/button";
import { Dropdown, type DropdownProps } from "@magnetic/dropdown";
import { Popover } from "@magnetic/popover";
import { Text } from "@magnetic/text";

import { Icon } from "src/components/icons";
import { DataProviderContext } from "src/core/provider/dataProvider";
import { SoftwareMode } from "src/gen/schema/models/fabric";
import { CandidateMode } from "src/utils/api/configD";

import { withShowToggle } from "./hoc/withShowToggle";

import "./buttons.scss";

/**
 * This makes name or id a mandatory prop.
 * It should help automation
 * tools identify buttons more easily.
 */
type RequireNameOrId =
  | { name: string; id?: never }
  | { id: string; name?: never }
  | { name: string; id: string };

/**
 * combine our RequireNameOrId type
 * with the MagneticButtonProps to
 * force name or id to be required
 * on buttons.
 */
type ButtonProps = MagneticButtonProps &
  RequireNameOrId &
  Omit<MagneticButtonProps, "name" | "id">;

type EditButtonProps = {
  /**
   * Need a way to ignore the software mode check
   * logic or the unbind button will not be disabled
   * when we are in SOFTWARE_MODE_MAINTENANCE_ON.
   * unbind button should always be allowed if
   * in edit mode
   */
  readonly ignoreSoftwareMode?: boolean;
  readonly tip?: string;
  readonly disabled?: boolean;
} & ButtonProps;

/**
 * This adds a requirement of name or id prop
 * for all buttons. It should help automation
 * tools identify buttons more easily.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props: ButtonProps, ref) => {
    return <MagneticButton ref={ref} {...props} />;
  }
) as ForwardRefExoticComponent<ButtonProps> & {
  Group: typeof MagneticButton.Group;
};
// 3. Add display name for better debugging
Button.displayName = "Button";
// 4. Attach static properties
Button.Group = MagneticButton.Group;

/**
 * A set of tooltips for the different candidate types
 */
const SOFTWARE_UPGRADE_MODE_TOOLTIP: string =
  "Fabric is in software upgrade mode. Configuration changes for this fabric have been disabled until the software upgrade is completed.";

const withSoftwareUpgradeCheck = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  return forwardRef((props: EditButtonProps, ref) => {
    const { ignoreSoftwareMode, ...rest } = props;
    const { softwareConfig } = useContext(DataProviderContext);
    const disableBasedOnSoftwareMode = !(
      softwareConfig?.softwareMode !==
        SoftwareMode.SOFTWARE_MODE_MAINTENANCE_ON || ignoreSoftwareMode
    );
    const disable = props.disabled || disableBasedOnSoftwareMode;
    const tip = disableBasedOnSoftwareMode
      ? SOFTWARE_UPGRADE_MODE_TOOLTIP
      : props.tip;
    return (
      <WrappedComponent
        {...(rest as P)}
        tip={tip}
        disabled={disable}
        ref={ref}
      />
    );
  });
};

const EditButton = withSoftwareUpgradeCheck(
  forwardRef<HTMLButtonElement, EditButtonProps>(
    (props: EditButtonProps, ref) => {
      const { children, tip, ...rest } = props;
      const { mode } = useContext(DataProviderContext);
      if (mode === CandidateMode.Edit) {
        if (tip) {
          return (
            <Popover
              kind="map"
              className="popover-tooltip"
              title={<Text size="p3">{tip}</Text>}
            >
              <Button name="editButton" ref={ref} {...rest}>
                {children}
              </Button>
            </Popover>
          );
        }

        return (
          <Button ref={ref} {...rest}>
            {children}
          </Button>
        );
      }
      return null;
    }
  )
);

type DropdownButtonProps = DropdownProps & {
  readonly tip?: string;
};

export const DropdownButton = (
  props: PropsWithChildren<DropdownButtonProps>
) => {
  // Destructure children and tip from props, rest contains other DropdownProps
  const { children, tip, ...rest } = props;
  // Get current mode from DataProviderContext
  const { mode } = useContext(DataProviderContext);

  const dropdown = (
    <>
      {/* Pass all remaining props to Dropdown, render children inside */}
      <Dropdown {...rest}>{children}</Dropdown>
    </>
  );

  // Only render the button if we're in Edit mode
  if (mode === CandidateMode.Edit) {
    // If a tip is provided, wrap Dropdown in a Popover for tooltip display
    if (tip) {
      return (
        <Popover
          kind="map"
          className="popover-tooltip"
          // Tooltip content uses Text component for consistent styling
          title={<Text size="p3">{tip}</Text>}
        >
          {dropdown}
        </Popover>
      );
    }
    // No tip: just render the Dropdown with props and children
    return dropdown;
  }
  // Not in Edit mode: don't render anything
  return null;
};

export { EditButton };

export const DetailsButton = withShowToggle((props: ButtonProps) => {
  return (
    <Button
      kind="secondary"
      className="details-btn"
      size="md"
      name="detailsActionBtn"
      data-qa="details-button"
      {...props}
    >
      Details
      <Icon name="caretLineLeft" />
    </Button>
  );
});
